import React, { useEffect, useState } from "react";

// material-ui
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Tooltip,
  CircularProgress,
  FormControlLabel,
  useTheme,
  Autocomplete,
  ListSubheader,
} from "@mui/material";

// third-party
import _ from "lodash";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// project imports
import CircularWithPath from "../../../../../../components/@extended/progress/CircularWithPath";
import { openSnackbar } from "../../../../../../api/snackbar";
import { useDebounce } from "../../../../../../hooks/useDebounce";

// assets
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

// types
import { SnackbarProps } from "../../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "../../../../../../api/userAxios";
import ReusableModal from "../../../../../../components/ReusableComponent/ReusableModal";
import {
  VacancySubscription,
  VacancySubscriptionFormData,
} from "../../../../../../types/orders-managment/subscriptions";
import {
  useUsers,
  useUsersForAutocomplete,
} from "../../../../../../hooks/query-hooks/useUsers";
import {
  useVacancyPackages,
  useVacancyPackagesForAutocomplete,
} from "../../../../../../hooks/query-hooks/useVacancyPackages";
import {
  useOrderItems,
  useOrderItemsForAutocomplete,
} from "../../../../../../hooks/query-hooks/useOrderItems";
import { packageTypeOptions } from "./mockData";

// constant
const getInitialValues = (
  vacancySubscription: VacancySubscription | null
): VacancySubscriptionFormData => {
  if (vacancySubscription) {
    return {
      user_id: vacancySubscription.user_id?.toString() || "",
      vacancy_package_id:
        vacancySubscription.vacancy_package_id?.toString() || "",
      order_item_id: vacancySubscription.order_item_id?.toString() || "",
      company_name: vacancySubscription.company_name || "",
      type: vacancySubscription.type || "",
      status: vacancySubscription.status || false,
      package_validation: vacancySubscription.package_validation || 0,
      paid_amount: vacancySubscription.paid_amount || 0,
      free_amount: vacancySubscription.free_amount || 0,
      amount_vacancies: vacancySubscription.amount_vacancies || 0,
      used_vacancies: vacancySubscription.used_vacancies || 0,
      start_date: vacancySubscription.start_date || "",
      end_date: vacancySubscription.end_date || "",
    };
  } else {
    return {
      user_id: "",
      vacancy_package_id: "",
      order_item_id: "",
      company_name: "",
      type: "",
      status: false,
      package_validation: 0,
      paid_amount: 0,
      free_amount: 0,
      amount_vacancies: 0,
      used_vacancies: 0,
      start_date: "",
      end_date: "",
    };
  }
};

// ==============================|| VACANCY SUBSCRIPTION ADD / EDIT - FORM ||============================== //

const AddEditVacancySubscription = ({
  open,
  vacancySubscription,
  id,
  loading,
  fetchData,
  setLoading,
  handleClose,
  setResult,
}: {
  open: boolean;
  loading: boolean;
  id: string;
  setLoading: any;
  fetchData: any;
  vacancySubscription: VacancySubscription | null;
  handleClose: () => void;
  setResult: any;
}) => {
  const intl = useIntl();
  const theme = useTheme();

  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);

  // Search states for autocomplete
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [packageSearchTerm, setPackageSearchTerm] = useState("");
  const [orderSearchTerm, setOrderSearchTerm] = useState("");

  // Debounced search terms
  const debouncedCompanySearchTerm = useDebounce(companySearchTerm, 500);
  const debouncedPackageSearchTerm = useDebounce(packageSearchTerm, 500);
  const debouncedOrderSearchTerm = useDebounce(orderSearchTerm, 500);

  // Selected values for autocompletes
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);

  const [serverErrors, setServerErrors] = useState<Record<string, string[]>>(
    {}
  );

  // Hooks with debounced search terms
  const {
    items: companies = [],
    loading: loadingCompanies,
    hasMore: hasMoreCompanies,
    loadMore: loadMoreCompanies,
    error: companiesError,
  } = useUsersForAutocomplete("COMPANY", 20, debouncedCompanySearchTerm);

  const {
    items: packages = [],
    loading: loadingPackages,
    hasMore: hasMorePackages,
    loadMore: loadMorePackages,
    error: packagesError,
  } = useVacancyPackagesForAutocomplete(20, debouncedPackageSearchTerm);

  const {
    items: orders = [],
    loading: loadingOrders,
    hasMore: hasMoreOrders,
    loadMore: loadMoreOrders,
    error: ordersError,
  } = useOrderItemsForAutocomplete(20, debouncedOrderSearchTerm);

  useEffect(() => {
    setLoading(false);
    // Clear server errors when vacancySubscription changes
    setServerErrors({});

    if (vacancySubscription) {
      setStartDate(
        vacancySubscription.start_date
          ? dayjs(vacancySubscription.start_date)
          : null
      );
      setEndDate(
        vacancySubscription.end_date
          ? dayjs(vacancySubscription.end_date)
          : null
      );

      // Set initial selected values for editing
      const company = companies.find(
        (c: any) => c.id.toString() === vacancySubscription.user_id?.toString()
      );
      const pkg = packages.find(
        (p: any) =>
          p.id.toString() === vacancySubscription.vacancy_package_id?.toString()
      );
      const order = orders.find(
        (o: any) =>
          o.id.toString() === vacancySubscription.order_item_id?.toString()
      );

      setSelectedCompany(company || null);
      setSelectedPackage(pkg || null);
      setSelectedOrder(order || null);
    } else {
      setStartDate(null);
      setEndDate(null);
      setSelectedCompany(null);
      setSelectedPackage(null);
      setSelectedOrder(null);
    }
  }, [vacancySubscription, companies, packages, orders]);

  const VacancySubscriptionSchema = Yup.object().shape({
    user_id: Yup.string().required("Company is required"),
    vacancy_package_id: Yup.string().required("Vacancy Package is required"),
    order_item_id: Yup.string().required("Order Item is required"),
    company_name: Yup.string().required("Company Name is required"),
    type: Yup.string().required("Type is required"),
    package_validation: Yup.number()
      .min(0, "Package validation must be positive")
      .required("Package validation is required"),
    paid_amount: Yup.number()
      .min(0, "Paid amount must be positive")
      .required("Paid amount is required"),
    free_amount: Yup.number()
      .min(0, "Free amount must be positive")
      .required("Free amount is required"),
    amount_vacancies: Yup.number()
      .min(0, "Amount vacancies must be positive")
      .required("Amount vacancies is required"),
    used_vacancies: Yup.number()
      .min(0, "Used vacancies must be positive")
      .required("Used vacancies is required"),
  });

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: dayjs.Dayjs | null) => {
    setEndDate(date);
  };

  const formik = useFormik({
    initialValues: getInitialValues(vacancySubscription!),
    validationSchema: VacancySubscriptionSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setServerErrors({});

      try {
        const subscriptionData = {
          user_id: parseInt(values.user_id),
          vacancy_package_id: parseInt(values.vacancy_package_id),
          order_item_id: parseInt(values.order_item_id),
          company_name: values.company_name,
          type: values.type,
          status: values.status,
          package_validation: values.package_validation,
          paid_amount: values.paid_amount,
          free_amount: values.free_amount,
          amount_vacancies: values.amount_vacancies,
          used_vacancies: values.used_vacancies,
          start_date: startDate ? startDate.format("YYYY-MM-DD") : null,
          end_date: endDate ? endDate.format("YYYY-MM-DD") : null,
        };

        if (vacancySubscription) {
          try {
            const response = await USERAXIOS.post(
              `/vacancy_subscriptions/${vacancySubscription?.id}`,
              subscriptionData
            );
            const updatedSubscription = response.data.data;

            if (updatedSubscription) {
              setResult((prev: any[]) =>
                prev.map((sub) =>
                  sub.id === updatedSubscription.id ? updatedSubscription : sub
                )
              );

              openSnackbar({
                open: true,
                message: "Vacancy Subscription updated successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: { color: "success" },
              } as SnackbarProps);
            }
            handleClose();
          } catch (error: any) {
            setServerErrors(error.response.data.message);

            openSnackbar({
              open: true,
              message: "Failed to update vacancy subscription",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "error" },
            } as SnackbarProps);
          }
        } else {
          try {
            const response = await USERAXIOS.post(
              `/vacancy_subscriptions`,
              subscriptionData
            );

            const newSubscription = response.data.data;

            if (newSubscription) {
              setResult((prev: any[]) => [...prev, newSubscription]);

              openSnackbar({
                open: true,
                message: "Vacancy Subscription added successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: { color: "success" },
              } as SnackbarProps);
            }
            handleClose();
          } catch (error: any) {
            setServerErrors(error.response.data.message);

            openSnackbar({
              open: true,
              message: "Failed to create vacancy subscription",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "error" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
      } catch (error: any) {
        console.error("Error submitting form:", error);
        openSnackbar({
          open: true,
          message: error?.response?.data?.message || "An error occurred",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  // Custom render function for autocomplete options with load more
  const renderOption = (
    props: any,
    option: any,
    { inputValue }: any,
    items: any[],
    loadMore: () => void,
    hasMore: boolean
  ) => {
    const index = items.findIndex((item) => item.id === option.id);
    const isLast = index === items.length - 1;

    return (
      <>
        <li {...props}>
          {option.user_name ||
            option.name ||
            option.title ||
            option.order_number ||
            `Item ${option.id}`}
        </li>
        {isLast && hasMore && (
          <ListSubheader
            component="div"
            onClick={loadMore}
            sx={{ cursor: "pointer", textAlign: "center", py: 1 }}
          >
            Load More...
          </ListSubheader>
        )}
      </>
    );
  };

  if (loading)
    return (
      <Box sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="center">
          <CircularWithPath />
        </Stack>
      </Box>
    );

  return (
    <ReusableModal open={open} handleClose={handleClose}>
      <FormikProvider value={formik}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle
              color="primary"
              style={{
                textAlign: "center",
                fontSize: "22px",
                fontWeight: "bold",
                marginBottom: "10px",
              }}
            >
              {vacancySubscription ? (
                <FormattedMessage id="Edit Vacancy Subscription" />
              ) : (
                <FormattedMessage id="Add Vacancy Subscription" />
              )}
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="user-autocomplete">
                      <FormattedMessage id="Company" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Select company"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <Autocomplete
                      id="user-autocomplete"
                      options={companies}
                      value={selectedCompany}
                      loading={loadingCompanies}
                      disabled={!!vacancySubscription}
                      getOptionLabel={(option) => option?.user_name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "input") {
                          setCompanySearchTerm(newInputValue);
                        }
                      }}
                      onChange={(event, newValue) => {
                        setFieldValue(
                          "user_id",
                          newValue?.id?.toString() || ""
                        );
                        setFieldValue(
                          "company_name",
                          newValue?.user_name || ""
                        );
                        // Clear search term when option is selected
                        if (newValue) {
                          setSelectedCompany(newValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search and select company"
                          error={Boolean(touched.user_id && errors.user_id)}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingCompanies ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option, state) =>
                        renderOption(
                          props,
                          option,
                          state,
                          companies,
                          loadMoreCompanies,
                          hasMoreCompanies
                        )
                      }
                      noOptionsText={
                        companiesError
                          ? "Error loading companies"
                          : companies.length === 0
                          ? "No companies available"
                          : "No options"
                      }
                    />
                    {touched.user_id && errors.user_id && (
                      <FormHelperText error>{errors.user_id}</FormHelperText>
                    )}
                    {serverErrors.user_id && (
                      <FormHelperText error>
                        {serverErrors.user_id}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="order-item-autocomplete">
                      <FormattedMessage id="Order" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Select order for the subscription"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <Autocomplete
                      id="order-item-autocomplete"
                      options={orders}
                      value={selectedOrder}
                      loading={loadingOrders}
                      disabled={!!vacancySubscription}
                      getOptionLabel={(option) => option?.id}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "input") {
                          setOrderSearchTerm(newInputValue);
                        }
                      }}
                      onChange={(event, newValue) => {
                        setFieldValue(
                          "order_item_id",
                          newValue?.id?.toString() || ""
                        );
                        // Clear search term when option is selected
                        if (newValue) {
                          setSelectedOrder(newValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search and select order"
                          error={Boolean(
                            touched.order_item_id && errors.order_item_id
                          )}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingOrders ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option, state) =>
                        renderOption(
                          props,
                          option,
                          state,
                          orders,
                          loadMoreOrders,
                          hasMoreOrders
                        )
                      }
                      noOptionsText={
                        ordersError
                          ? "Error loading orders"
                          : orders.length === 0
                          ? "No orders available"
                          : "No options"
                      }
                    />
                    {touched.order_item_id && errors.order_item_id && (
                      <FormHelperText error>
                        {errors.order_item_id}
                      </FormHelperText>
                    )}
                    {serverErrors.order_item_id && (
                      <FormHelperText error>
                        {serverErrors.order_item_id}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="subscription-type">
                      <FormattedMessage id="Type" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Select subscription type"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <Select
                      disabled={!!vacancySubscription}
                      id="subscription-type"
                      displayEmpty
                      {...getFieldProps("type")}
                      onChange={(event: SelectChangeEvent<string>) => {
                        setFieldValue("type", event.target.value);
                      }}
                      error={Boolean(touched.type && errors.type)}
                    >
                      {packageTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {theme.direction === "ltr"
                            ? option.label
                            : option.arabic_label}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.type && errors.type && (
                      <FormHelperText error>{errors.type}</FormHelperText>
                    )}
                    {serverErrors.type && (
                      <FormHelperText error>{serverErrors.type}</FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="vacancy-package-autocomplete">
                      <FormattedMessage id="Package" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Select vacancy package"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <Autocomplete
                      disabled={!!vacancySubscription}
                      id="vacancy-package-autocomplete"
                      options={packages}
                      value={selectedPackage}
                      loading={loadingPackages}
                      getOptionLabel={(option) =>
                        option?.name ||
                        option?.title ||
                        option?.description ||
                        `Package ${option?.id}` ||
                        ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "input") {
                          setPackageSearchTerm(newInputValue);
                        }
                      }}
                      onChange={(event, newValue) => {
                        setFieldValue(
                          "vacancy_package_id",
                          newValue?.id?.toString() || ""
                        );
                        // Clear search term when option is selected
                        if (newValue) {
                          setSelectedPackage(newValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search and select package"
                          error={Boolean(
                            touched.vacancy_package_id &&
                              errors.vacancy_package_id
                          )}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingPackages ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option, state) =>
                        renderOption(
                          props,
                          option,
                          state,
                          packages,
                          loadMorePackages,
                          hasMorePackages
                        )
                      }
                      noOptionsText={
                        packagesError
                          ? "Error loading packages"
                          : packages.length === 0
                          ? "No packages available"
                          : "No options"
                      }
                    />
                    {touched.vacancy_package_id &&
                      errors.vacancy_package_id && (
                        <FormHelperText error>
                          {errors.vacancy_package_id}
                        </FormHelperText>
                      )}
                    {serverErrors.vacancy_package_id && (
                      <FormHelperText error>
                        {serverErrors.vacancy_package_id}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="package-validation">
                      <FormattedMessage id="Package Validity" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Enter package validation period"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="package-validation"
                      placeholder="Enter Package Validation"
                      type="number"
                      {...getFieldProps("package_validation")}
                      error={Boolean(
                        touched.package_validation && errors.package_validation
                      )}
                      helperText={
                        touched.package_validation && errors.package_validation
                      }
                    />
                    {serverErrors.package_validation && (
                      <FormHelperText error>
                        {serverErrors.package_validation}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="amount-vacancies">
                      <FormattedMessage id="Total Vacancies" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Enter total amount of vacancies"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="amount-vacancies"
                      placeholder="Enter Amount Vacancies"
                      type="number"
                      {...getFieldProps("amount_vacancies")}
                      error={Boolean(
                        touched.amount_vacancies && errors.amount_vacancies
                      )}
                      helperText={
                        touched.amount_vacancies && errors.amount_vacancies
                      }
                    />
                    {serverErrors.amount_vacancies && (
                      <FormHelperText error>
                        {serverErrors.amount_vacancies}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="used-vacancies">
                      <FormattedMessage id="Used Vacancies" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Enter used vacancies"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="used-vacancies"
                      placeholder="Enter Used Vacancies"
                      type="number"
                      {...getFieldProps("used_vacancies")}
                      error={Boolean(
                        touched.used_vacancies && errors.used_vacancies
                      )}
                      helperText={
                        touched.used_vacancies && errors.used_vacancies
                      }
                    />
                    {serverErrors.used_vacancies && (
                      <FormHelperText error>
                        {serverErrors.used_vacancies}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="paid-amount">
                      <FormattedMessage id="Paid Amount" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Enter paid amount"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="paid-amount"
                      placeholder="Enter Paid Amount"
                      type="number"
                      {...getFieldProps("paid_amount")}
                      error={Boolean(touched.paid_amount && errors.paid_amount)}
                      helperText={touched.paid_amount && errors.paid_amount}
                    />
                    {serverErrors.paid_amount && (
                      <FormHelperText error>
                        {serverErrors.paid_amount}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="free-amount">
                      <FormattedMessage id="Free Amount" />
                      <span style={{ color: "red" }}>*</span>
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Enter free amount"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="free-amount"
                      placeholder="Enter Free Amount"
                      type="number"
                      {...getFieldProps("free_amount")}
                      error={Boolean(touched.free_amount && errors.free_amount)}
                      helperText={touched.free_amount && errors.free_amount}
                    />
                    {serverErrors.free_amount && (
                      <FormHelperText error>
                        {serverErrors.free_amount}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6} sx={{ display: "none" }}>
                  <TextField
                    fullWidth
                    id="company-name"
                    {...getFieldProps("company_name")}
                  />
                </Grid>
                {serverErrors.company_name && (
                  <Grid item xs={12}>
                    <FormHelperText error>
                      {serverErrors.company_name}
                    </FormHelperText>
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="start-date">
                      <FormattedMessage id="Start Date" />
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Select start date"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <DatePicker
                      value={startDate}
                      onChange={handleDateChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          id: "start-date",
                        },
                      }}
                    />
                    {serverErrors.start_date && (
                      <FormHelperText error>
                        {serverErrors.start_date}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="end-date">
                      <FormattedMessage id="End Date" />
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Select end date"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <DatePicker
                      value={endDate}
                      onChange={handleEndDateChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          id: "end-date",
                        },
                      }}
                    />
                    {serverErrors.end_date && (
                      <FormHelperText error>
                        {serverErrors.end_date}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="subscription-status">
                      <FormattedMessage id="Status" />
                      <span style={{ color: "grey", marginLeft: "5px" }}>
                        <Tooltip title={"Set subscription status"}>
                          <HelpOutlineOutlinedIcon
                            style={{
                              width: "15px",
                              height: "15px",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    </InputLabel>
                    <FormControl fullWidth>
                      <FormControlLabel
                        control={
                          <Switch
                            id="subscription-status"
                            checked={formik.values.status}
                            {...getFieldProps("status")}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setFieldValue("status", checked);
                            }}
                          />
                        }
                        label={
                          formik.values.status ? (
                            <FormattedMessage id="Active" />
                          ) : (
                            <FormattedMessage id="Inactive" />
                          )
                        }
                      />
                    </FormControl>
                    {touched.status && errors.status && (
                      <FormHelperText error sx={{ pl: 1.75 }}>
                        {errors.status}
                      </FormHelperText>
                    )}
                    {serverErrors.status && (
                      <FormHelperText error sx={{ pl: 1.75 }}>
                        {serverErrors.status}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={6}>
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage
                        id={vacancySubscription ? "Update" : "Submit"}
                      />
                    </Button>
                  </Grid>
                  <Grid item md={6}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
    </ReusableModal>
  );
};

export default AddEditVacancySubscription;

// import React, { useEffect, useState } from "react";

// // material-ui
// import {
//   Box,
//   Button,
//   DialogActions,
//   DialogContent,
//   DialogTitle,
//   Divider,
//   FormControl,
//   Grid,
//   FormHelperText,
//   InputLabel,
//   MenuItem,
//   Select,
//   SelectChangeEvent,
//   Stack,
//   Switch,
//   TextField,
//   Tooltip,
//   CircularProgress,
//   FormControlLabel,
//   useTheme,
// } from "@mui/material";

// // third-party
// import _ from "lodash";
// import * as Yup from "yup";
// import { useFormik, Form, FormikProvider } from "formik";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import dayjs from "dayjs";

// // project imports
// import CircularWithPath from "../../../../../../components/@extended/progress/CircularWithPath";
// import { openSnackbar } from "../../../../../../api/snackbar";

// // assets
// import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

// // types
// import { SnackbarProps } from "../../../../../../types/snackbar";
// import { FormattedMessage, useIntl } from "react-intl";
// import { USERAXIOS } from "../../../../../../api/userAxios";
// import ReusableModal from "../../../../../../components/ReusableComponent/ReusableModal";
// import {
//   VacancySubscription,
//   VacancySubscriptionFormData,
// } from "../../../../../../types/orders-managment/subscriptions";
// import {
//   useUsers,
//   useUsersForAutocomplete,
// } from "../../../../../../hooks/query-hooks/useUsers";
// import {
//   useVacancyPackages,
//   useVacancyPackagesForAutocomplete,
// } from "../../../../../../hooks/query-hooks/useVacancyPackages";
// import {
//   useOrderItems,
//   useOrderItemsForAutocomplete,
// } from "../../../../../../hooks/query-hooks/useOrderItems";
// import { packageTypeOptions } from "./mockData";

// // constant
// const getInitialValues = (
//   vacancySubscription: VacancySubscription | null
// ): VacancySubscriptionFormData => {
//   if (vacancySubscription) {
//     return {
//       user_id: vacancySubscription.user_id?.toString() || "",
//       vacancy_package_id:
//         vacancySubscription.vacancy_package_id?.toString() || "",
//       order_item_id: vacancySubscription.order_item_id?.toString() || "",
//       company_name: vacancySubscription.company_name || "",
//       type: vacancySubscription.type || "",
//       status: vacancySubscription.status || false,
//       package_validation: vacancySubscription.package_validation || 0,
//       paid_amount: vacancySubscription.paid_amount || 0,
//       free_amount: vacancySubscription.free_amount || 0,
//       amount_vacancies: vacancySubscription.amount_vacancies || 0,
//       used_vacancies: vacancySubscription.used_vacancies || 0,
//       start_date: vacancySubscription.start_date || "",
//       end_date: vacancySubscription.end_date || "",
//     };
//   } else {
//     return {
//       user_id: "",
//       vacancy_package_id: "",
//       order_item_id: "",
//       company_name: "",
//       type: "",
//       status: false,
//       package_validation: 0,
//       paid_amount: 0,
//       free_amount: 0,
//       amount_vacancies: 0,
//       used_vacancies: 0,
//       start_date: "",
//       end_date: "",
//     };
//   }
// };

// // ==============================|| VACANCY SUBSCRIPTION ADD / EDIT - FORM ||============================== //

// const AddEditVacancySubscription = ({
//   open,
//   vacancySubscription,
//   id,
//   loading,
//   fetchData,
//   setLoading,
//   handleClose,
//   setResult,
// }: {
//   open: boolean;
//   loading: boolean;
//   id: string;
//   setLoading: any;
//   fetchData: any;
//   vacancySubscription: VacancySubscription | null;
//   handleClose: () => void;
//   setResult: any;
// }) => {
//   const intl = useIntl();
//   const theme = useTheme();

//   const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
//   const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);

//   const [serverErrors, setServerErrors] = useState<Record<string, string[]>>(
//     {}
//   );

//   const {
//     data: companies = [],
//     isLoading: companiesLoading,
//     error: companiesError,
//   } = useUsersForAutocomplete("COMPANY");
//   const {
//     data: packages = [],
//     isLoading: packagesLoading,
//     error: packagesError,
//   } = useVacancyPackagesForAutocomplete();
//   const {
//     data: orders = [],
//     isLoading: ordersLoading,
//     error: ordersError,
//   } = useOrderItemsForAutocomplete();

//   useEffect(() => {
//     setLoading(false);
//     // Clear server errors when vacancySubscription changes
//     setServerErrors({});

//     if (vacancySubscription) {
//       setStartDate(
//         vacancySubscription.start_date
//           ? dayjs(vacancySubscription.start_date)
//           : null
//       );
//       setEndDate(
//         vacancySubscription.end_date
//           ? dayjs(vacancySubscription.end_date)
//           : null
//       );
//     } else {
//       setStartDate(null);
//       setEndDate(null);
//     }
//   }, [vacancySubscription]);

//   const VacancySubscriptionSchema = Yup.object().shape({
//     user_id: Yup.string().required("Company is required"),
//     vacancy_package_id: Yup.string().required("Vacancy Package is required"),
//     order_item_id: Yup.string().required("Order Item is required"),
//     company_name: Yup.string().required("Company Name is required"),
//     type: Yup.string().required("Type is required"),
//     package_validation: Yup.number()
//       .min(0, "Package validation must be positive")
//       .required("Package validation is required"),
//     paid_amount: Yup.number()
//       .min(0, "Paid amount must be positive")
//       .required("Paid amount is required"),
//     free_amount: Yup.number()
//       .min(0, "Free amount must be positive")
//       .required("Free amount is required"),
//     amount_vacancies: Yup.number()
//       .min(0, "Amount vacancies must be positive")
//       .required("Amount vacancies is required"),
//     used_vacancies: Yup.number()
//       .min(0, "Used vacancies must be positive")
//       .required("Used vacancies is required"),
//   });

//   const handleDateChange = (date: dayjs.Dayjs | null) => {
//     setStartDate(date);
//   };

//   const handleEndDateChange = (date: dayjs.Dayjs | null) => {
//     setEndDate(date);
//   };

//   const formik = useFormik({
//     initialValues: getInitialValues(vacancySubscription!),
//     validationSchema: VacancySubscriptionSchema,
//     enableReinitialize: true,
//     onSubmit: async (values, { setSubmitting }) => {
//       setServerErrors({});

//       try {
//         const subscriptionData = {
//           user_id: parseInt(values.user_id),
//           vacancy_package_id: parseInt(values.vacancy_package_id),
//           order_item_id: parseInt(values.order_item_id),
//           company_name: values.company_name,
//           type: values.type,
//           status: values.status,
//           package_validation: values.package_validation,
//           paid_amount: values.paid_amount,
//           free_amount: values.free_amount,
//           amount_vacancies: values.amount_vacancies,
//           used_vacancies: values.used_vacancies,
//           start_date: startDate ? startDate.format("YYYY-MM-DD") : null,
//           end_date: endDate ? endDate.format("YYYY-MM-DD") : null,
//         };

//         if (vacancySubscription) {
//           try {
//             const response = await USERAXIOS.post(
//               `/vacancy_subscriptions/${vacancySubscription?.id}`,
//               subscriptionData
//             );
//             const updatedSubscription = response.data.data;

//             if (updatedSubscription) {
//               setResult((prev: any[]) =>
//                 prev.map((sub) =>
//                   sub.id === updatedSubscription.id ? updatedSubscription : sub
//                 )
//               );

//               openSnackbar({
//                 open: true,
//                 message: "Vacancy Subscription updated successfully",
//                 anchorOrigin: { vertical: "top", horizontal: "right" },
//                 variant: "alert",
//                 alert: { color: "success" },
//               } as SnackbarProps);
//             }
//             handleClose();
//           } catch (error: any) {
//             setServerErrors(error.response.data.message);

//             openSnackbar({
//               open: true,
//               message: "Failed to update vacancy subscription",
//               anchorOrigin: { vertical: "top", horizontal: "right" },
//               variant: "alert",
//               alert: { color: "error" },
//             } as SnackbarProps);
//           }
//         } else {
//           try {
//             const response = await USERAXIOS.post(
//               `/vacancy_subscriptions`,
//               subscriptionData
//             );

//             const newSubscription = response.data.data;

//             if (newSubscription) {
//               setResult((prev: any[]) => [...prev, newSubscription]);

//               openSnackbar({
//                 open: true,
//                 message: "Vacancy Subscription added successfully",
//                 anchorOrigin: { vertical: "top", horizontal: "right" },
//                 variant: "alert",
//                 alert: { color: "success" },
//               } as SnackbarProps);
//             }
//             handleClose();
//           } catch (error: any) {
//             setServerErrors(error.response.data.message);

//             openSnackbar({
//               open: true,
//               message: "Failed to create vacancy subscription",
//               anchorOrigin: { vertical: "top", horizontal: "right" },
//               variant: "alert",
//               alert: { color: "error" },
//             } as SnackbarProps);
//           }
//         }

//         setSubmitting(false);
//       } catch (error: any) {
//         console.error("Error submitting form:", error);
//         openSnackbar({
//           open: true,
//           message: error?.response?.data?.message || "An error occurred",
//           anchorOrigin: { vertical: "top", horizontal: "right" },
//           variant: "alert",
//           alert: { color: "error" },
//         } as SnackbarProps);
//         setSubmitting(false);
//       }
//     },
//   });

//   const {
//     errors,
//     touched,
//     handleSubmit,
//     isSubmitting,
//     getFieldProps,
//     setFieldValue,
//   } = formik;

//   if (loading)
//     return (
//       <Box sx={{ p: 5 }}>
//         <Stack direction="row" justifyContent="center">
//           <CircularWithPath />
//         </Stack>
//       </Box>
//     );

//   return (
//     <ReusableModal open={open} handleClose={handleClose}>
//       <FormikProvider value={formik}>
//         <LocalizationProvider dateAdapter={AdapterDayjs}>
//           <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
//             <DialogTitle
//               color="primary"
//               style={{
//                 textAlign: "center",
//                 fontSize: "22px",
//                 fontWeight: "bold",
//                 marginBottom: "10px",
//               }}
//             >
//               {vacancySubscription ? (
//                 <FormattedMessage id="Edit Vacancy Subscription" />
//               ) : (
//                 <FormattedMessage id="Add Vacancy Subscription" />
//               )}
//             </DialogTitle>
//             <Divider />
//             <DialogContent sx={{ p: 2.5 }}>
//               <Grid container spacing={2}>
//                 <Grid item xs={12} md={6}>
//                   <Stack spacing={1}>
//                     <InputLabel htmlFor="order-item-id">
//                       <FormattedMessage id="Order" />
//                       <span style={{ color: "red" }}>*</span>
//                       <span style={{ color: "grey", marginLeft: "5px" }}>
//                         <Tooltip title={"Select order for the subscription"}>
//                           <HelpOutlineOutlinedIcon
//                             style={{
//                               width: "15px",
//                               height: "15px",
//                               cursor: "pointer",
//                             }}
//                           />
//                         </Tooltip>
//                       </span>
//                     </InputLabel>
//                     <Select
//                       id="order-item-id"
//                       displayEmpty
//                       {...getFieldProps("order_item_id")}
//                       onChange={(event: SelectChangeEvent<string>) => {
//                         setFieldValue("order_item_id", event.target.value);
//                       }}
//                       error={Boolean(
//                         touched.order_item_id && errors.order_item_id
//                       )}
//                       disabled={ordersLoading || !!vacancySubscription}
//                     >
//                       {ordersLoading ? (
//                         <MenuItem disabled>Loading orders...</MenuItem>
//                       ) : ordersError ? (
//                         <MenuItem disabled>Error loading orders</MenuItem>
//                       ) : orders.length === 0 ? (
//                         <MenuItem disabled>No orders available</MenuItem>
//                       ) : (
//                         orders.map((order: any) => (
//                           <MenuItem key={order.id} value={order.id.toString()}>
//                             {order.order_number || order.id}
//                           </MenuItem>
//                         ))
//                       )}
//                     </Select>
//                     {touched.order_item_id && errors.order_item_id && (
//                       <FormHelperText error>
//                         {errors.order_item_id}
//                       </FormHelperText>
//                     )}
//                     {serverErrors.order_item_id && (
//                       <FormHelperText error>
//                         {serverErrors.order_item_id}
//                       </FormHelperText>
//                     )}
//                   </Stack>
//                 </Grid>

//                 {/* Company */}
//                 <Grid item xs={12} md={6}>
//                   <Stack spacing={1}>
//                     <InputLabel htmlFor="user-id">
//                       <FormattedMessage id="Company" />
//                       <span style={{ color: "red" }}>*</span>
//                       <span style={{ color: "grey", marginLeft: "5px" }}>
//                         <Tooltip title={"Select company"}>
//                           <HelpOutlineOutlinedIcon
//                             style={{
//                               width: "15px",
//                               height: "15px",
//                               cursor: "pointer",
//                             }}
//                           />
//                         </Tooltip>
//                       </span>
//                     </InputLabel>
//                     <Select
//                       id="user-id"
//                       displayEmpty
//                       {...getFieldProps("user_id")}
//                       onChange={(event: SelectChangeEvent<string>) => {
//                         const selectedId = event.target.value;
//                         const selectedCompany = companies.find(
//                           (company) => company.id.toString() === selectedId
//                         );
//                         setFieldValue("user_id", selectedId);
//                         setFieldValue(
//                           "company_name",
//                           selectedCompany?.user_name || ""
//                         );
//                       }}
//                       error={Boolean(touched.user_id && errors.user_id)}
//                       disabled={companiesLoading || !!vacancySubscription}
//                     >
//                       {companiesLoading ? (
//                         <MenuItem disabled>Loading companies...</MenuItem>
//                       ) : companiesError ? (
//                         <MenuItem disabled>Error loading companies</MenuItem>
//                       ) : companies.length === 0 ? (
//                         <MenuItem disabled>No companies available</MenuItem>
//                       ) : (
//                         companies.map((company: any) => (
//                           <MenuItem
//                             key={company.id}
//                             value={company.id.toString()}
//                           >
//                             {company.user_name}
//                           </MenuItem>
//                         ))
//                       )}
//                     </Select>
//                     {touched.user_id && errors.user_id && (
//                       <FormHelperText error>{errors.user_id}</FormHelperText>
//                     )}
//                     {serverErrors.user_id && (
//                       <FormHelperText error>
//                         {serverErrors.user_id}
//                       </FormHelperText>
//                     )}
//                   </Stack>
//                 </Grid>

//                 {/* Type */}
//                 <Grid item xs={12} md={6}>
//                   <Stack spacing={1}>
//                     <InputLabel htmlFor="subscription-type">
//                       <FormattedMessage id="Type" />
//                       <span style={{ color: "red" }}>*</span>
//                       <span style={{ color: "grey", marginLeft: "5px" }}>
//                         <Tooltip title={"Select subscription type"}>
//                           <HelpOutlineOutlinedIcon
//                             style={{
//                               width: "15px",
//                               height: "15px",
//                               cursor: "pointer",
//                             }}
//                           />
//                         </Tooltip>
//                       </span>
//                     </InputLabel>
//                     <Select
//                       id="subscription-type"
//                       displayEmpty
//                       {...getFieldProps("type")}
//                       onChange={(event: SelectChangeEvent<string>) => {
//                         setFieldValue("type", event.target.value);
//                       }}
//                       error={Boolean(touched.type && errors.type)}
//                     >
//                       {packageTypeOptions.map((option) => (
//                         <MenuItem key={option.value} value={option.value}>
//                           {theme.direction === "ltr"
//                             ? option.label
//                             : option.arabic_label}
//                         </MenuItem>
//                       ))}
//                     </Select>
//                     {touched.type && errors.type && (
//                       <FormHelperText error>{errors.type}</FormHelperText>
//                     )}
//                     {serverErrors.type && (
//                       <FormHelperText error>{serverErrors.type}</FormHelperText>
//                     )}
//                   </Stack>
//                 </Grid>

//                 <Grid item xs={12} md={6}>
//                   <Stack spacing={1}>
//                     <InputLabel htmlFor="vacancy-package-id">
//                       <FormattedMessage id="Package" />
//                       <span style={{ color: "red" }}>*</span>
//                       <span style={{ color: "grey", marginLeft: "5px" }}>
//                         <Tooltip title={"Select vacancy package"}>
//                           <HelpOutlineOutlinedIcon
//                             style={{
//                               width: "15px",
//                               height: "15px",
//                               cursor: "pointer",
//                             }}
//                           />
//                         </Tooltip>
//                       </span>
//                     </InputLabel>
//                     <Select
//                       id="vacancy-package-id"
//                       displayEmpty
//                       {...getFieldProps("vacancy_package_id")}
//                       onChange={(event: SelectChangeEvent<string>) => {
//                         setFieldValue("vacancy_package_id", event.target.value);
//                       }}
//                       error={Boolean(
//                         touched.vacancy_package_id && errors.vacancy_package_id
//                       )}
//                       disabled={packagesLoading}
//                     >
//                       {packagesLoading ? (
//                         <MenuItem disabled>Loading packages...</MenuItem>
//                       ) : packagesError ? (
//                         <MenuItem disabled>Error loading packages</MenuItem>
//                       ) : packages.length === 0 ? (
//                         <MenuItem disabled>No packages available</MenuItem>
//                       ) : (
//                         packages.map((pkg: any) => (
//                           <MenuItem key={pkg.id} value={pkg.id.toString()}>
//                             {pkg.name ||
//                               pkg.title ||
//                               pkg.description ||
//                               `Package ${pkg.id}`}
//                           </MenuItem>
//                         ))
//                       )}
//                     </Select>
//                     {touched.vacancy_package_id &&
//                       errors.vacancy_package_id && (
//                         <FormHelperText error>
//                           {errors.vacancy_package_id}
//                         </FormHelperText>
//                       )}
//                     {serverErrors.vacancy_package_id && (
//                       <FormHelperText error>
//                         {serverErrors.vacancy_package_id}
//                       </FormHelperText>
//                     )}
//                   </Stack>
//                 </Grid>

//                 <Grid item xs={12} md={6}>
//                   <Stack spacing={1}>
//                     <InputLabel htmlFor="package-validation">
//                       <FormattedMessage id="Package Validity" />
//                       <span style={{ color: "red" }}>*</span>
//                       <span style={{ color: "grey", marginLeft: "5px" }}>
//                         <Tooltip title={"Enter package validation period"}>
//                           <HelpOutlineOutlinedIcon
//                             style={{
//                               width: "15px",
//                               height: "15px",
//                               cursor: "pointer",
//                             }}
//                           />
//                         </Tooltip>
//                       </span>
//                     </InputLabel>
//                     <TextField
//                       fullWidth
//                       id="package-validation"
//                       placeholder="Enter Package Validation"
//                       type="number"
//                       {...getFieldProps("package_validation")}
//                       error={Boolean(
//                         touched.package_validation && errors.package_validation
//                       )}
//                       helperText={
//                         touched.package_validation && errors.package_validation
//                       }
//                     />
//                     {serverErrors.package_validation && (
//                       <FormHelperText error>
//                         {serverErrors.package_validation}
//                       </FormHelperText>
//                     )}
//                   </Stack>
//                 </Grid>

//                 {/* Paid Amount */}
//                 <Grid item xs={12} md={6}>
//                   <Stack spacing={1}>
//                     <InputLabel htmlFor="paid-amount">
//                       <FormattedMessage id="Paid Amount" />
//                       <span style={{ color: "red" }}>*</span>
//                       <span style={{ color: "grey", marginLeft: "5px" }}>
//                         <Tooltip title={"Enter paid amount"}>
//                           <HelpOutlineOutlinedIcon
//                             style={{
//                               width: "15px",
//                               height: "15px",
//                               cursor: "pointer",
//                             }}
//                           />
//                         </Tooltip>
//                       </span>
//                     </InputLabel>
//                     <TextField
//                       fullWidth
//                       id="paid-amount"
//                       placeholder="Enter Paid Amount"
//                       type="number"
//                       {...getFieldProps("paid_amount")}
//                       error={Boolean(touched.paid_amount && errors.paid_amount)}
//                       helperText={touched.paid_amount && errors.paid_amount}
//                     />
//                     {serverErrors.paid_amount && (
//                       <FormHelperText error>
//                         {serverErrors.paid_amount}
//                       </FormHelperText>
//                     )}
//                   </Stack>
//                 </Grid>

//                 {/* Free Amount */}
//                 <Grid item xs={12} md={6}>
//                   <Stack spacing={1}>
//                     <InputLabel htmlFor="free-amount">
//                       <FormattedMessage id="Free Amount" />
//                       <span style={{ color: "red" }}>*</span>
//                       <span style={{ color: "grey", marginLeft: "5px" }}>
//                         <Tooltip title={"Enter free amount"}>
//                           <HelpOutlineOutlinedIcon
//                             style={{
//                               width: "15px",
//                               height: "15px",
//                               cursor: "pointer",
//                             }}
//                           />
//                         </Tooltip>
//                       </span>
//                     </InputLabel>
//                     <TextField
//                       fullWidth
//                       id="free-amount"
//                       placeholder="Enter Free Amount"
//                       type="number"
//                       {...getFieldProps("free_amount")}
//                       error={Boolean(touched.free_amount && errors.free_amount)}
//                       helperText={touched.free_amount && errors.free_amount}
//                     />
//                     {serverErrors.free_amount && (
//                       <FormHelperText error>
//                         {serverErrors.free_amount}
//                       </FormHelperText>
//                     )}
//                   </Stack>
//                 </Grid>

//                 {/* Amount Vacancies */}
//                 <Grid item xs={12} md={6}>
//                   <Stack spacing={1}>
//                     <InputLabel htmlFor="amount-vacancies">
//                       <FormattedMessage id="Total Vacancies" />
//                       <span style={{ color: "red" }}>*</span>
//                       <span style={{ color: "grey", marginLeft: "5px" }}>
//                         <Tooltip title={"Enter total amount of vacancies"}>
//                           <HelpOutlineOutlinedIcon
//                             style={{
//                               width: "15px",
//                               height: "15px",
//                               cursor: "pointer",
//                             }}
//                           />
//                         </Tooltip>
//                       </span>
//                     </InputLabel>
//                     <TextField
//                       fullWidth
//                       id="amount-vacancies"
//                       placeholder="Enter Amount Vacancies"
//                       type="number"
//                       {...getFieldProps("amount_vacancies")}
//                       error={Boolean(
//                         touched.amount_vacancies && errors.amount_vacancies
//                       )}
//                       helperText={
//                         touched.amount_vacancies && errors.amount_vacancies
//                       }
//                     />
//                     {serverErrors.amount_vacancies && (
//                       <FormHelperText error>
//                         {serverErrors.amount_vacancies}
//                       </FormHelperText>
//                     )}
//                   </Stack>
//                 </Grid>

//                 {/* Used Vacancies */}
//                 <Grid item xs={12} md={6}>
//                   <Stack spacing={1}>
//                     <InputLabel htmlFor="used-vacancies">
//                       <FormattedMessage id="Used Vacancies" />
//                       <span style={{ color: "red" }}>*</span>
//                       <span style={{ color: "grey", marginLeft: "5px" }}>
//                         <Tooltip title={"Enter used vacancies"}>
//                           <HelpOutlineOutlinedIcon
//                             style={{
//                               width: "15px",
//                               height: "15px",
//                               cursor: "pointer",
//                             }}
//                           />
//                         </Tooltip>
//                       </span>
//                     </InputLabel>
//                     <TextField
//                       fullWidth
//                       id="used-vacancies"
//                       placeholder="Enter Used Vacancies"
//                       type="number"
//                       {...getFieldProps("used_vacancies")}
//                       error={Boolean(
//                         touched.used_vacancies && errors.used_vacancies
//                       )}
//                       helperText={
//                         touched.used_vacancies && errors.used_vacancies
//                       }
//                     />
//                     {serverErrors.used_vacancies && (
//                       <FormHelperText error>
//                         {serverErrors.used_vacancies}
//                       </FormHelperText>
//                     )}
//                   </Stack>
//                 </Grid>

//                 {/* Company Name (Hidden field that gets auto-filled) */}
//                 <Grid item xs={12} md={6} sx={{ display: "none" }}>
//                   <TextField
//                     fullWidth
//                     id="company-name"
//                     {...getFieldProps("company_name")}
//                   />
//                 </Grid>
//                 {serverErrors.company_name && (
//                   <Grid item xs={12}>
//                     <FormHelperText error>
//                       {serverErrors.company_name}
//                     </FormHelperText>
//                   </Grid>
//                 )}

//                 {/* Start Date */}
//                 <Grid item xs={12} md={6}>
//                   <Stack spacing={1}>
//                     <InputLabel htmlFor="start-date">
//                       <FormattedMessage id="Start Date" />
//                       <span style={{ color: "grey", marginLeft: "5px" }}>
//                         <Tooltip title={"Select start date"}>
//                           <HelpOutlineOutlinedIcon
//                             style={{
//                               width: "15px",
//                               height: "15px",
//                               cursor: "pointer",
//                             }}
//                           />
//                         </Tooltip>
//                       </span>
//                     </InputLabel>
//                     <DatePicker
//                       value={startDate}
//                       onChange={handleDateChange}
//                       slotProps={{
//                         textField: {
//                           fullWidth: true,
//                           id: "start-date",
//                         },
//                       }}
//                     />
//                     {serverErrors.start_date && (
//                       <FormHelperText error>
//                         {serverErrors.start_date}
//                       </FormHelperText>
//                     )}
//                   </Stack>
//                 </Grid>

//                 {/* End Date */}
//                 <Grid item xs={12} md={6}>
//                   <Stack spacing={1}>
//                     <InputLabel htmlFor="end-date">
//                       <FormattedMessage id="End Date" />
//                       <span style={{ color: "grey", marginLeft: "5px" }}>
//                         <Tooltip title={"Select end date"}>
//                           <HelpOutlineOutlinedIcon
//                             style={{
//                               width: "15px",
//                               height: "15px",
//                               cursor: "pointer",
//                             }}
//                           />
//                         </Tooltip>
//                       </span>
//                     </InputLabel>
//                     <DatePicker
//                       value={endDate}
//                       onChange={handleEndDateChange}
//                       slotProps={{
//                         textField: {
//                           fullWidth: true,
//                           id: "end-date",
//                         },
//                       }}
//                     />
//                     {serverErrors.end_date && (
//                       <FormHelperText error>
//                         {serverErrors.end_date}
//                       </FormHelperText>
//                     )}
//                   </Stack>
//                 </Grid>

//                 {/* Status */}
//                 <Grid item xs={12}>
//                   <Stack spacing={1}>
//                     <InputLabel htmlFor="subscription-status">
//                       <FormattedMessage id="Status" />
//                       <span style={{ color: "grey", marginLeft: "5px" }}>
//                         <Tooltip title={"Set subscription status"}>
//                           <HelpOutlineOutlinedIcon
//                             style={{
//                               width: "15px",
//                               height: "15px",
//                               cursor: "pointer",
//                             }}
//                           />
//                         </Tooltip>
//                       </span>
//                     </InputLabel>
//                     <FormControl fullWidth>
//                       <FormControlLabel
//                         control={
//                           <Switch
//                             id="subscription-status"
//                             checked={formik.values.status}
//                             {...getFieldProps("status")}
//                             onChange={(event) => {
//                               const checked = event.target.checked;
//                               setFieldValue("status", checked);
//                             }}
//                           />
//                         }
//                         label={
//                           formik.values.status ? (
//                             <FormattedMessage id="Active" />
//                           ) : (
//                             <FormattedMessage id="Inactive" />
//                           )
//                         }
//                       />
//                     </FormControl>
//                     {touched.status && errors.status && (
//                       <FormHelperText error sx={{ pl: 1.75 }}>
//                         {errors.status}
//                       </FormHelperText>
//                     )}
//                     {serverErrors.status && (
//                       <FormHelperText error sx={{ pl: 1.75 }}>
//                         {serverErrors.status}
//                       </FormHelperText>
//                     )}
//                   </Stack>
//                 </Grid>
//               </Grid>
//             </DialogContent>
//             <Divider />
//             <DialogActions sx={{ p: 2.5 }}>
//               <Grid container justifyContent="center" alignItems="center">
//                 <Stack direction="row" spacing={2} sx={{ width: 1 }}>
//                   <Grid item md={2} />
//                   <Grid item md={6}>
//                     <Button
//                       disabled={isSubmitting}
//                       fullWidth
//                       type="submit"
//                       variant="contained"
//                     >
//                       <FormattedMessage
//                         id={vacancySubscription ? "Update" : "Submit"}
//                       />
//                     </Button>
//                   </Grid>
//                   <Grid item md={6}>
//                     <Button
//                       fullWidth
//                       color="secondary"
//                       variant="outlined"
//                       onClick={handleClose}
//                     >
//                       <FormattedMessage id="Cancel" />
//                     </Button>
//                   </Grid>
//                   <Grid item md={2} />
//                 </Stack>
//               </Grid>
//             </DialogActions>
//           </Form>
//         </LocalizationProvider>
//       </FormikProvider>
//     </ReusableModal>
//   );
// };

// export default AddEditVacancySubscription;
